import React, { useRef, useState, useEffect, forwardRef } from 'react';
import MonacoEditor from 'react-monaco-editor';
import { IconButton, Tooltip } from '@mui/material';
import { Maximize2, Minimize2, Copy, Check, ExternalLink } from 'lucide-react';

const MonacoEditorWithFullscreen = forwardRef((props, ref) => {
  const {
    value,
    onChange,
    language = 'javascript',
    theme = 'vs-dark',
    options = {},
    height = '300px',
    width = '100%',
    showFullscreenButton = true,
    showCopyButton = true,
    logMediaUrl,
    editorDidMount,
    ...rest
  } = props;

  const containerRef = useRef(null);
  const editorRef = useRef(null);
  const resizeObserverRef = useRef(null);
  const layoutTimeoutRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [copied, setCopied] = useState(false);

  // Forward ref to the Monaco editor instance if needed
  useEffect(() => {
    if (ref && editorRef.current) {
      if (typeof ref === 'function') {
        ref(editorRef.current);
      } else {
        ref.current = editorRef.current;
      }
    }
  }, [ref, editorRef.current]);

  // Setup ResizeObserver
  useEffect(() => {
    if (!containerRef.current) return;

    const debouncedLayout = () => {
      if (layoutTimeoutRef.current) {
        clearTimeout(layoutTimeoutRef.current);
      }
      layoutTimeoutRef.current = setTimeout(() => {
        if (editorRef.current) {
          try {
            editorRef.current.layout();
          } catch (error) {
            console.error('Error during editor layout:', error);
          }
        }
      }, 100);
    };

    resizeObserverRef.current = new ResizeObserver(() => {
      debouncedLayout();
    });

    resizeObserverRef.current.observe(containerRef.current);

    return () => {
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
      }
      if (layoutTimeoutRef.current) {
        clearTimeout(layoutTimeoutRef.current);
      }
    };
  }, []);

  // Handle fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      const isFullscreenNow = !!document.fullscreenElement;
      setIsFullscreen(isFullscreenNow);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('msfullscreenchange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('msfullscreenchange', handleFullscreenChange);
    };
  }, []);

  // Handle Fullscreen API
  const handleToggleFullscreen = () => {
    if (!isFullscreen) {
      const el = containerRef.current;
      if (el) {
        try {
          if (el.requestFullscreen) el.requestFullscreen();
          else if (el.webkitRequestFullscreen) el.webkitRequestFullscreen();
          else if (el.msRequestFullscreen) el.msRequestFullscreen();
        } catch (error) {
          console.error('Error entering fullscreen:', error);
        }
      }
    } else {
      try {
        if (document.exitFullscreen) document.exitFullscreen();
        else if (document.webkitExitFullscreen) document.webkitExitFullscreen();
        else if (document.msExitFullscreen) document.msExitFullscreen();
      } catch (error) {
        console.error('Error exiting fullscreen:', error);
      }
    }
  };

  // Editor did mount handler
  const handleEditorDidMount = (editor, monaco) => {
    editorRef.current = editor;
    if (typeof editorDidMount === 'function') {
      editorDidMount(editor, monaco);
    }
  };

  // Copy to clipboard handler
  const handleCopy = async () => {
    let textToCopy = value;
    if (editorRef.current && (value === undefined || value === null)) {
      textToCopy = editorRef.current.getValue();
    }
    try {
      await navigator.clipboard.writeText(textToCopy || '');
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (e) {
      // fallback: do nothing
    }
  };

  return (
    <div
      ref={containerRef}
      style={{
        position: isFullscreen ? 'fixed' : 'relative',
        top: isFullscreen ? 0 : undefined,
        left: isFullscreen ? 0 : undefined,
        width: isFullscreen ? '100vw' : width,
        height: isFullscreen ? '100vh' : height,
        zIndex: isFullscreen ? 1300 : 'auto',
        background: 'var(--background-primary, #1e1e1e)',
        boxShadow: isFullscreen ? 'none' : '0 2px 8px rgba(0,0,0,0.1)',
        overflow: 'hidden',
      }}
    >
      <div style={{ position: 'absolute', top: 4, right: 5, zIndex: 2, display: 'flex', gap: 4, flexDirection: "column" }}>
        {logMediaUrl && (
          <Tooltip title="Open Full Code" placement='top'>
            <IconButton
              onClick={() => logMediaUrl && window.open(logMediaUrl, "_blank")}
              size="small"
              sx={{
                color: copied ? 'var(--primary-color, #4ade80)' : 'var(--text-primary, #fff)',
                background: 'rgba(0,0,0,0.2)',
                '&:hover': {
                  background: 'rgba(0,0,0,0.4)',
                  color: 'var(--text-primary, #fff)'
                },
                padding: 1,
              }}
            >
              <ExternalLink size={13} />
            </IconButton>
          </Tooltip>
        )}
        {showCopyButton && (
          <Tooltip title={copied ? 'Copied!' : 'Copy to clipboard'}>
            <IconButton
              onClick={handleCopy}
              size="small"
              sx={{
                color: copied ? 'var(--primary-color, #4ade80)' : 'var(--text-primary, #fff)',
                background: 'rgba(0,0,0,0.2)',
                '&:hover': {
                  background: 'rgba(0,0,0,0.4)',
                  color: 'var(--text-primary, #fff)'
                },
                padding: 1,
              }}
            >
              {copied ? <Check size={13} /> : <Copy size={13} />}
            </IconButton>
          </Tooltip>
        )}
        {showFullscreenButton && (
          <Tooltip title={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}>
            <IconButton
              onClick={handleToggleFullscreen}
              size="small"
              sx={{
                color: 'var(--text-primary, #fff)',
                background: 'rgba(0,0,0,0.2)',
                '&:hover': {
                  background: 'rgba(0,0,0,0.4)',
                  color: 'var(--text-primary, #fff)'
                },
                padding: 1,
              }}
            >
              {isFullscreen ? <Minimize2 size={13} /> : <Maximize2 size={13} />}
            </IconButton>
          </Tooltip>
        )}
      </div>
      <MonacoEditor
        value={value}
        onChange={onChange}
        language={language}
        theme={theme}
        options={{
          ...options,
          automaticLayout: false, // Disable automatic layout
          padding: { top: 25 }
        }}
        width="100%"
        height={isFullscreen ? '100vh' : height}
        editorDidMount={handleEditorDidMount}
        {...rest}
      />
    </div>
  );
});

export default MonacoEditorWithFullscreen; 